(ns ui.view.transaction-process.cards.property-list
  "Home of `field-row` — the scalar/keyword/currency value-field renderer reused
   by the generic field card."
  (:require
   [common.eid :as eid]
   [common.localization :as loc]
   [common.schema.display-name :as display-name]
   [common.schema.field :as field]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.updates.entity-scope :as entity-scope]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.modals.entity-selector.view :as entity-selector]
   [ui.view.transaction-process.event :as event]
   [ui.view.transaction-process.util :as tp-util]))

(defn read-only-field
  "Display-only renderer for a computed/calculated field: the same label as
   `field-row`, with the formatted value shown as plain text instead of a greyed
   disabled input. Currency values include their symbol (e.g. \"EUR 12\"); an
   uncomputable value (no components yet, or mixed currencies) shows nothing."
  [{:keys [field-kw field-config entity-data]}]
  (let [value (tp-util/format-field-value {:field-kw    field-kw
                                           :field-cfg   field-config
                                           :entity-data entity-data})]
    [:div {:class "flex flex-col gap-1.5"}
     [labelled-input/label-elem {:label (or (:field/label field-config)
                                            (tp-util/error-text
                                             {:context (str "Missing :field/label for " field-kw)
                                              :value   field-kw}))
                                 :class "min-h-9 flex items-end"}]
     ;; `py-2 text-md` mirrors the number input's padding + font size, so the
     ;; value lands at the same height as an editable field's text (tight to the
     ;; label, not floating) and the grid rows line up.
     [:div {:class "py-2 text-md m-0 text-gray-900 font-medium"}
      (when (cu/non-empty-string? value) value)]]))

(defn- ref->option
  "Materialize a stored ref value (db-id / {:db/id n}) into the {:value :label}
   shape the entity-search picker expects, resolving the display name from the
   session. Returns nil for a blank value.

   The label is never the raw id. A new-grid account does not bulk-sync the
   referenced company or person into the session, so the entity can be missing;
   the label is then a loading text until `fetch-missing-refs!` loads it. The
   search editor throws when a value has no label, so there is always one."
  [value]
  (when (some? value)
    (let [id (eid/eid value)
          entity (session/get-entity id)]
      {:value id
       :label (if entity
                (display-name/compute {:entity entity})
                (loc/tr :loading))})))

(defn- ref-ids
  "The referenced entity ids of a ref field value, single or multi."
  [data-type value]
  (if (= data-type :data.type/ref-multi)
    (->> (or value []) (keep eid/eid) vec)
    (when (some? value)
      [(eid/eid value)])))

(defn fetch-missing-refs!
  "Load every referenced entity that the session does not have yet, so the picker
   shows the name and not a loading text. A new-grid account does not bulk-sync
   the referenced company or person into the session. `load-entity!` merges the
   fetch into the session, so a re-render then resolves the name.

   `requested*` is the set of ids already requested. Each id is loaded at most
   once, also when the load fails. A failed load (for example a 403 or a 404 for
   an entity that the account cannot read) must not start again on the next
   render, so the id stays in the set and the picker keeps the loading text."
  [requested* ids]
  (doseq [id ids]
    (when (and id
               (not (session/get-entity id))
               (not (contains? @requested* id)))
      (swap! requested* conj id)
      (entity-scope/load-entity!
       {:entity-id id
        :on-loaded (fn [_entity _entities] nil)
        :on-error  (fn [_error] nil)}))))

(defn- ref-field
  "Entity-search picker for a ref field (CRM company/person lookup). The picker
   needs each value as a {:value :label} option. On a new-grid account the
   referenced entity is not in the session, so load every referenced id on
   demand; the picker shows a loading text until the name arrives, never the raw
   id."
  [{:keys [base-props ctx field-kw field-config value create!]}]
  (r/with-let [requested* (r/atom #{})]
    (let [data-type    (:field/data-type field-config)
          multi?       (= data-type :data.type/ref-multi)
          picker-value (if multi?
                         (->> (or value []) (keep ref->option) vec)
                         (ref->option value))]
      (fetch-missing-refs! requested* (ref-ids data-type value))
      [labelled-input/view
       (merge base-props
              {:editor-type               :editor.type/search
               :value                     picker-value
               :field/select-entity-types (or (:field/select-entity-types field-config)
                                              (:field/entity-types field-config))
               :create!                   create!
               :on-change                 (fn [selected & _]
                                            (rf/dispatch [::event/update-draft-field ctx field-kw selected]))}
              (select-keys field-config
                           [:field/option-filter-fn
                            :field/os-filter-terms-fn]))])))

(defn field-notice
  [{:keys [field-config entity-data]}]
  (when-let [{:notice/keys [text-key] :as notice-config}
             (:field/notice field-config)]
    (when (tp-util/field-visible? {:field-config notice-config
                                   :value-lookup entity-data})
      [alert/warning
       {}
       [:p {:class "m-0"}
        (loc/tr text-key)]])))

(defn field-row
  [{:keys [ctx field-kw field-config entity-data permission mandatory?]}]
  (let [label     (or (:field/label field-config)
                      (tp-util/error-text {:context (str "Missing :field/label for " field-kw)
                                           :value field-kw}))
        data-type (:field/data-type field-config)
        read-only? (= permission :r)
        value     (get entity-data field-kw)
        on-change (fn [new-val]
                    (rf/dispatch [::event/update-draft-field ctx field-kw new-val]))
        create!   (when (and (:create-on-fly? field-config)
                             (not read-only?))
                    (fn [query]
                      (entity-selector/open-and-select!
                       (field/create-entity-types field-config)
                       {:start-value       query
                        :attribute         field-kw
                        :skip-success-msg? true
                        :on-select!        (fn [new-entity-id _new-option]
                                             (on-change new-entity-id))})))
        ref?      (contains? #{:data.type/ref-single :data.type/ref-multi} data-type)
        options   (when (#{:data.type/keyword :data.type/keyword-multi} data-type)
                    (:field/options field-config))
        base-props (cond-> {:label      label
                            :data-type  data-type
                            :value      value
                            :on-change  on-change
                            :disabled?  read-only?
                            :mandatory? (boolean mandatory?)
                            :label-class "min-h-9 flex items-end"}
                     (:field/placeholder field-config)
                     (assoc :placeholder (:field/placeholder field-config))

                     (seq options)
                     (assoc :options options)

                     (and (= :data.type/percentage data-type)
                          (not (:field/uncapped-percentage? field-config)))
                     (assoc :min 0
                            :max 100))]
    (cond
      (= data-type :data.type/currency-value)
      (let [sym-kw (cu/currency->symbol field-kw)
            sym-val (get entity-data sym-kw)]
        [labelled-input/currency-view
         (assoc base-props
                :secondary-args {:data-type :data.type/currency-symbol
                                 :value     (or sym-val @session/default-currency)
                                 :disabled? read-only?
                                 :on-change (fn [v] (rf/dispatch [::event/update-draft-field ctx sym-kw v]))})])

      ref?
      ;; Ref fields render as an entity-search picker that loads a missing
      ;; referenced entity on demand; see `ref-field`.
      [ref-field {:base-props   base-props
                  :ctx          ctx
                  :field-kw     field-kw
                  :field-config field-config
                  :value        value
                  :create!      create!}]

      :else
      [labelled-input/view base-props])))
